import { Box, Container, Theme } from "@radix-ui/themes";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
// Disabled along with FacebookPixelProvider/TikTokPixelProvider in main.tsx:
// only needed on campaign landing pages, none of which are hosted here.
// import { usePageView } from "react-facebook";
import { useBoolVariation } from "@launchdarkly/react-sdk";
import { HomePage } from "./routes/pages/HomePage";
import { WelcomePage } from "./routes/pages/WelcomePage";
// import { useTikTokPageView } from "./lib/TikTokPixelProvider";
import { useGoogleAnalyticsPageView } from "./lib/GoogleAnalyticsProvider";
import { CognitoAuthProvider, RequireAuth } from "./lib/CognitoAuthProvider";
import { WaitlistModalProvider } from "./components/WaitlistModal";
import { AtmosBackground } from "./components/AtmosBackground";

/**
 * External Stripe billing portal used when the in-app subscription management
 * page is disabled via the `website-subscription-managent-enabled` flag.
 */
const STRIPE_BILLING_PORTAL_URL =
  "https://billing.stripe.com/p/login/aFa6oG7hDgWK74b3JDgnK00";

function HamburgerIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

const LearnMorePage = lazy(() =>
  import("./routes/pages/LearnMorePage").then((module) => ({
    default: module.LearnMorePage,
  })),
);

const TermsPage = lazy(() =>
  import("./routes/pages/TermsPage").then((module) => ({
    default: module.TermsPage,
  })),
);

const PrivacyPage = lazy(() =>
  import("./routes/pages/PrivacyPage").then((module) => ({
    default: module.PrivacyPage,
  })),
);

const SafetyProtocolPage = lazy(() =>
  import("./routes/pages/SafetyProtocolPage").then((module) => ({
    default: module.SafetyProtocolPage,
  })),
);

const CheckoutPage = lazy(() =>
  import("./routes/pages/CheckoutPage").then((module) => ({
    default: module.CheckoutPage,
  })),
);

const SubscriptionPage = lazy(() =>
  import("./routes/pages/SubscriptionPage").then((module) => ({
    default: module.SubscriptionPage,
  })),
);

const FlourishingSurveyPage = lazy(() =>
  import("./routes/pages/FlourishingSurveyPage").then((module) => ({
    default: module.FlourishingSurveyPage,
  })),
);

function PageViewTracker() {
  // Facebook/TikTok pixel tracking disabled (see main.tsx) - only needed on
  // campaign landing pages, none of which are hosted here.
  // usePageView({ trackOnMount: true, trackOnRouteChange: true });
  // useTikTokPageView();
  useGoogleAnalyticsPageView();

  return null;
}

function PageContent() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="learn-more" element={<LearnMorePage />} />
        <Route path="terms-conditions" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="safety-protocol" element={<SafetyProtocolPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route
          path="subscription"
          element={
            <CognitoAuthProvider>
              <RequireAuth>
                <SubscriptionPage />
              </RequireAuth>
            </CognitoAuthProvider>
          }
        />
        <Route
          path="exp/flourishing/v1"
          element={
            <CognitoAuthProvider>
              <RequireAuth>
                <FlourishingSurveyPage />
              </RequireAuth>
            </CognitoAuthProvider>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  // Single app-wide root Theme. WaitlistModalProvider's own <Theme> (for the
  // dialog, rendered as a sibling to AppContent) and WelcomePage's <Theme>
  // then nest inside this one instead of each being their own root - Radix
  // Themes gives every *root* Theme `min-height: 100dvh`, so multiple
  // sibling root Themes previously stacked and doubled the page height.
  return (
    <Theme
      appearance="light"
      accentColor="gray"
      grayColor="gray"
      radius="large"
    >
      <WaitlistModalProvider>
        <AppContent />
      </WaitlistModalProvider>
    </Theme>
  );
}

function AppContent() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  // When enabled, the "Manage Subscription" links point at the in-app
  // subscription management page; otherwise they fall back to the external
  // Stripe billing portal.
  const subscriptionManagementEnabled = useBoolVariation(
    "website-subscription-managent-enabled",
    false,
  );

  // Welcome page renders without the shared nav layout
  if (location.pathname === "/welcome" || location.pathname === "/welcome/") {
    return <WelcomePage />;
  }

  // Flourishing survey is a focused, single-task flow opened straight from a
  // text message deep link - the shared brand/nav header (and its hamburger
  // menu) doesn't apply here. This covers all /exp/* experiment pages, not
  // just this one. No wrapping frame either - these pages manage their own
  // full-bleed background and padding.
  if (location.pathname === "/exp" || location.pathname.startsWith("/exp/")) {
    return (
      <>
        <PageViewTracker />
        <PageContent />
      </>
    );
  }

  return (
    <>
      <PageViewTracker />
      <Box className={isHome ? "page home-page" : "page"}>
        {isHome && <AtmosBackground />}
        <Box className={isHome ? "hero-section" : ""}>
          <Container size="4" className="frame">
            <div className="container-6 w-container">
              <a
                href="/"
                aria-current={isHome ? "page" : undefined}
                className="brand w-nav-brand w--current"
                aria-label="home"
              >
                <div className="div-block-8">
                  <img
                    className="brand-icon"
                    loading="lazy"
                    alt=""
                    src="/iris-speech-bubbles-dark.svg"
                  />
                  <div className="links">
                    <div className="link">
                      <span className="logo-wordmark">IRIS</span>
                    </div>
                  </div>
                </div>
              </a>

              {/* Desktop nav */}
              <nav
                role="navigation"
                className="nav-menu w-nav-menu nav-desktop"
                aria-label="Primary links"
              >
                <div className="w-layout-vflex flex-block-5">
                  <div className="links">
                    <a href="/learn-more/" className="link">
                      <span className="lnk">About</span>
                    </a>
                  </div>
                  <div className="links">
                    {subscriptionManagementEnabled ? (
                      <a href="/subscription/" className="link">
                        <span className="lnk">Log In</span>
                      </a>
                    ) : (
                      <a
                        href={STRIPE_BILLING_PORTAL_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="link"
                      >
                        <span className="lnk">Log In</span>
                      </a>
                    )}
                  </div>
                  <div className="links">
                    <a href="mailto:support@chatwithiris.com" className="link">
                      <span className="lnk">Contact</span>
                    </a>
                  </div>
                </div>
              </nav>

              {/* Mobile hamburger menu */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    className="nav-hamburger"
                    aria-label="Open navigation menu"
                  >
                    <HamburgerIcon />
                  </button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Portal>
                  <DropdownMenu.Content className="nav-dropdown" sideOffset={8}>
                    <DropdownMenu.Item asChild>
                      <a href="/learn-more/" className="nav-dropdown-link">
                        About
                      </a>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                      {subscriptionManagementEnabled ? (
                        <a href="/subscription/" className="nav-dropdown-link">
                          Log In
                        </a>
                      ) : (
                        <a
                          href={STRIPE_BILLING_PORTAL_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="nav-dropdown-link"
                        >
                          Log In
                        </a>
                      )}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                      <a
                        href="mailto:support@chatwithiris.com"
                        className="nav-dropdown-link"
                      >
                        Contact
                      </a>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>

            <main>
              <PageContent />
            </main>
          </Container>
        </Box>
      </Box>
    </>
  );
}
